import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  MessagesSquare, Plus, Send, Smile, Paperclip, Hash, Users, Search, AtSign,
  Sparkles, Pencil, Trash2, Reply, X, CornerDownRight, Circle, Loader2, Check, ChevronLeft
} from "lucide-react";
import { teamChatApi, api } from "../api";
import { chatBus } from "../chatSocket";
import { useAuth } from "../AuthContext";

const QUICK_EMOJIS = ["👍", "❤️", "🎉", "🚀", "😂", "🙏", "🔥", "👀"];

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

function RoomIcon({ room, online }) {
  if (room.type === "space") {
    return (
      <span className="inline-flex w-7 h-7 items-center justify-center rounded-sm bg-[#8b5cf6]/15 text-[#a78bfa] border border-[#8b5cf6]/30">
        <Hash size={13} />
      </span>
    );
  }
  const initial = (room.other?.name || room.other?.address || "?").charAt(0).toUpperCase();
  return (
    <span className="relative inline-flex w-7 h-7 items-center justify-center rounded-sm bg-[#3b82f6]/15 text-[#3b82f6] border border-[#3b82f6]/30 mono text-[11px] font-medium">
      {initial}
      {online && (
        <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#10b981] border border-[#0b1014]" />
      )}
    </span>
  );
}

function NewRoomModal({ onClose, onCreated }) {
  const [tab, setTab] = useState("dm");
  const [dir, setDir] = useState([]);
  const [filter, setFilter] = useState("");
  const [spaceName, setSpaceName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/users/directory").then(r => setDir(r.data || [])).catch(() => setDir([]));
  }, []);

  const filtered = dir.filter(u => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (u.address || "").includes(f) || (u.name || "").toLowerCase().includes(f);
  });

  const toggle = (addr) => {
    if (tab === "dm") { setSelected([addr]); return; }
    setSelected(s => s.includes(addr) ? s.filter(x => x !== addr) : [...s, addr]);
  };

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      if (tab === "dm") {
        if (!selected[0]) throw new Error("Pick a teammate to DM.");
        const room = await teamChatApi.createDM(selected[0]);
        onCreated(room);
      } else {
        if (!spaceName.trim()) throw new Error("Space name required.");
        if (selected.length === 0) throw new Error("Add at least one teammate to the space.");
        const room = await teamChatApi.createSpace(spaceName.trim(), selected);
        onCreated(room);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to create.");
    }
    setBusy(false);
  };

  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-30" onClick={onClose}>
      <div
        className="w-[440px] p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          border: "1px solid rgba(139,92,246,0.25)",
          clipPath: "polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="mono text-[11px] uppercase tracking-widest text-[#8b5cf6]">// new conversation</div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={12} /></button>
        </div>
        <div className="flex gap-1 mono text-[10px] uppercase tracking-widest">
          <button
            data-testid="newroom-tab-dm"
            onClick={() => { setTab("dm"); setSelected([]); }}
            className={`px-3 py-1 border ${tab === "dm" ? "border-[#8b5cf6]/60 bg-[#8b5cf6]/15 text-[#a78bfa]" : "border-white/10 text-white/55"}`}
          >Direct Message</button>
          <button
            data-testid="newroom-tab-space"
            onClick={() => { setTab("space"); setSelected([]); }}
            className={`px-3 py-1 border ${tab === "space" ? "border-[#8b5cf6]/60 bg-[#8b5cf6]/15 text-[#a78bfa]" : "border-white/10 text-white/55"}`}
          >Group Space</button>
        </div>

        {tab === "space" && (
          <input
            data-testid="newroom-space-name"
            value={spaceName}
            onChange={(e) => setSpaceName(e.target.value)}
            placeholder="Space name (e.g. Engineering, Sales)"
            className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/40"
          />
        )}

        <div className="border border-white/10 rounded-sm bg-black/30">
          <div className="flex items-center gap-1 border-b border-white/10 px-2">
            <Search size={11} className="text-white/40" />
            <input
              data-testid="newroom-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="search teammates"
              className="flex-1 bg-transparent py-1.5 text-[11px] outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-2 py-2 text-[10px] text-white/40 italic">No teammates found.</div>
            )}
            {filtered.map(u => {
              const isSel = selected.includes(u.address);
              return (
                <button
                  key={u.address}
                  data-testid={`newroom-row-${u.address}`}
                  onClick={() => toggle(u.address)}
                  className={`w-full text-left px-2 py-1.5 text-[11px] flex items-center justify-between hover:bg-white/5 ${isSel ? "bg-[#8b5cf6]/10" : ""}`}
                >
                  <span className="truncate">
                    <span className="text-white/85">{u.name || u.username}</span>
                    <span className="text-white/45 ml-2 mono">{u.address}</span>
                  </span>
                  {isSel && <Check size={11} className="text-[#8b5cf6]" />}
                </button>
              );
            })}
          </div>
        </div>

        {err && <div className="mono text-[10px] text-[#fca5a5]">// err: {err}</div>}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button
            data-testid="newroom-submit"
            onClick={submit}
            disabled={busy}
            className="btn-accent text-xs flex items-center gap-1"
          >
            {busy && <Loader2 size={11} className="animate-spin" />}
            {tab === "dm" ? "Start DM" : "Create Space"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, isMine, onReply, onEdit, onDelete, onReact, senderName }) {
  const [showActions, setShowActions] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);

  if (msg.deleted) {
    return (
      <div className="text-[10px] mono text-white/35 italic px-3">
        — message deleted by {senderName} —
      </div>
    );
  }
  if (msg.system) {
    return (
      <div data-testid={`msg-${msg.id}`} className="text-[10px] mono uppercase tracking-widest text-[#c7c1ff]/45 text-center py-1">
        {senderName} {msg.body}
      </div>
    );
  }

  const reactions = msg.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([_, list]) => list.length > 0);

  return (
    <div
      data-testid={`msg-${msg.id}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { setShowActions(false); setEmojiOpen(false); }}
      className={`group relative flex ${isMine ? "justify-end" : "justify-start"} px-3 py-1`}
    >
      <div className={`max-w-[72%] ${isMine ? "items-end" : "items-start"} flex flex-col`}>
        {!isMine && (
          <div className="text-[10px] mono uppercase tracking-widest text-[#c7c1ff]/55 mb-0.5 px-1">
            {senderName} <span className="text-white/30 normal-case ml-1">{fmtTime(msg.created)}</span>
          </div>
        )}

        {msg.reply_to_preview && (
          <div className="border-l-2 border-[#8b5cf6]/40 pl-2 mb-1 text-[10px] text-white/45 italic">
            <CornerDownRight size={9} className="inline mr-1" />{msg.reply_to_preview}
          </div>
        )}

        <div
          className={`px-3 py-1.5 text-sm whitespace-pre-wrap break-words ${
            isMine
              ? "bg-[#8b5cf6]/15 text-[#e6fbff] border border-[#8b5cf6]/30"
              : "bg-white/6 text-white border border-white/10"
          }`}
          style={{
            clipPath: isMine
              ? "polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)"
              : "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)",
          }}
        >
          {msg.body}
          {msg.edited && <span className="text-[9px] text-white/35 ml-2 mono">(edited)</span>}
          {(msg.attachments || []).map((a, i) => (
            <div key={i} className="mt-1.5">
              {a.mime?.startsWith("image/") ? (
                <img src={a.data} alt={a.name} className="max-w-[260px] max-h-[200px] rounded-sm border border-white/10" />
              ) : (
                <a
                  href={a.data}
                  download={a.name}
                  className="inline-flex items-center gap-1 text-[11px] text-[#a78bfa] hover:underline mono"
                >
                  <Paperclip size={10} /> {a.name}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Reactions row */}
        {reactionEntries.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1 px-1">
            {reactionEntries.map(([emoji, list]) => (
              <button
                key={emoji}
                onClick={() => onReact(msg.id, emoji)}
                className="text-[10px] px-1.5 py-0.5 bg-white/8 hover:bg-white/15 border border-white/15 rounded-sm"
              >
                {emoji} {list.length}
              </button>
            ))}
          </div>
        )}

        {isMine && (
          <div className="text-[9px] text-white/30 mono px-1 mt-0.5">{fmtTime(msg.created)}</div>
        )}
      </div>

      {/* Hover actions */}
      {showActions && (
        <div className={`absolute ${isMine ? "right-3" : "left-3"} -top-2 flex items-center gap-0.5 bg-[#0b1014] border border-[#8b5cf6]/30 px-1 py-0.5 z-10`}>
          <button
            data-testid={`msg-react-${msg.id}`}
            onClick={() => setEmojiOpen(o => !o)}
            className="p-1 hover:bg-[#8b5cf6]/15 text-white/70 hover:text-[#8b5cf6]"
            title="React"
          ><Smile size={11} /></button>
          <button
            data-testid={`msg-reply-${msg.id}`}
            onClick={() => onReply(msg)}
            className="p-1 hover:bg-[#8b5cf6]/15 text-white/70 hover:text-[#8b5cf6]"
            title="Reply"
          ><Reply size={11} /></button>
          {isMine && (
            <>
              <button
                data-testid={`msg-edit-${msg.id}`}
                onClick={() => onEdit(msg)}
                className="p-1 hover:bg-[#8b5cf6]/15 text-white/70 hover:text-[#8b5cf6]"
                title="Edit"
              ><Pencil size={11} /></button>
              <button
                data-testid={`msg-delete-${msg.id}`}
                onClick={() => onDelete(msg)}
                className="p-1 hover:bg-[#f87171]/20 text-white/70 hover:text-[#f87171]"
                title="Delete"
              ><Trash2 size={11} /></button>
            </>
          )}
        </div>
      )}

      {emojiOpen && (
        <div className={`absolute ${isMine ? "right-3" : "left-3"} -top-9 flex items-center gap-0.5 bg-[#0b1014] border border-[#8b5cf6]/30 px-1 py-0.5 z-20`}>
          {QUICK_EMOJIS.map(e => (
            <button
              key={e}
              onClick={() => { onReact(msg.id, e); setEmojiOpen(false); }}
              className="px-1 py-0.5 hover:bg-[#8b5cf6]/15 text-base"
              title={`React ${e}`}
            >{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TeamChat() {
  const { user } = useAuth();
  const me = user?.address || "";

  const [rooms, setRooms] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [typingFrom, setTypingFrom] = useState({});  // {address: timeoutId}
  const [presence, setPresence] = useState(new Set());
  const [showSlash, setShowSlash] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileRef = useRef(null);
  const scrollerRef = useRef(null);
  const composerRef = useRef(null);

  const activeRoom = useMemo(() => rooms.find(r => r.id === activeId) || null, [rooms, activeId]);

  // ---- Load rooms ----
  const loadRooms = useCallback(async () => {
    try {
      const data = await teamChatApi.listRooms();
      setRooms(data);
      return data;
    } catch { return []; }
  }, []);

  // ---- Load messages for active room ----
  const loadMessages = useCallback(async (rid) => {
    if (!rid) { setMessages([]); return; }
    try {
      const data = await teamChatApi.messages(rid, 200);
      setMessages(data);
      // Mark as read
      teamChatApi.markRead(rid).catch(() => {});
    } catch { setMessages([]); }
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);
  useEffect(() => { loadMessages(activeId); }, [activeId, loadMessages]);

  // ---- WebSocket: connect + subscribe ----
  useEffect(() => {
    const token = localStorage.getItem("aura_token");
    if (token) chatBus.connect(token);
    const unsub = chatBus.subscribe((evt) => {
      if (evt.type === "presence") {
        setPresence(new Set(evt.online || []));
      } else if (evt.type === "message") {
        if (evt.room_id === activeId) {
          setMessages(m => [...m, evt.message]);
          // Auto-mark-read if I'm currently viewing this room and msg isn't from me
          if (evt.message.sender !== me) teamChatApi.markRead(evt.room_id).catch(() => {});
        }
        // Refresh sidebar for unread/preview
        loadRooms();
      } else if (evt.type === "edit") {
        if (evt.room_id === activeId) {
          setMessages(m => m.map(x => x.id === evt.message.id ? evt.message : x));
        }
      } else if (evt.type === "delete") {
        if (evt.room_id === activeId) {
          setMessages(m => m.map(x => x.id === evt.msg_id ? { ...x, deleted: true, body: "", attachments: [] } : x));
        }
      } else if (evt.type === "reaction") {
        if (evt.room_id === activeId) {
          setMessages(m => m.map(x => x.id === evt.msg_id ? { ...x, reactions: evt.reactions } : x));
        }
      } else if (evt.type === "typing") {
        if (evt.room_id === activeId && evt.from !== me) {
          setTypingFrom(prev => {
            if (prev[evt.from]) clearTimeout(prev[evt.from]);
            const id = setTimeout(() => {
              setTypingFrom(p => { const c = { ...p }; delete c[evt.from]; return c; });
            }, 3000);
            return { ...prev, [evt.from]: id };
          });
        }
      } else if (evt.type === "room_updated") {
        loadRooms();
      }
    });
    return () => { unsub(); };
  }, [activeId, me, loadRooms]);

  // ---- Auto-scroll on new messages ----
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, activeId]);

  // ---- Composer helpers ----
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const out = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { alert(`${f.name} exceeds 5MB.`); continue; }
      const data = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => res(null);
        r.readAsDataURL(f);
      });
      if (data) out.push({ name: f.name, mime: f.type || "application/octet-stream", size: f.size, data });
    }
    setAttachments(a => [...a, ...out]);
    e.target.value = "";
  };

  const send = async () => {
    if (sending) return;
    if (editing) return saveEdit();
    const body = draft.trim();
    if (!body && attachments.length === 0) return;
    if (!activeRoom) return;
    setSending(true);
    try {
      // Preview reply text in body so backend stores reply_to and UI can render it.
      let preview = null;
      if (replyTo) {
        preview = (replyTo.body || "").slice(0, 80);
      }
      const msg = await teamChatApi.send(activeRoom.id, body, replyTo?.id || null, attachments);
      if (preview) msg.reply_to_preview = preview;
      // Optimistic add (WS will dedupe by id if it arrives)
      setMessages(m => m.some(x => x.id === msg.id) ? m : [...m, msg]);
      setDraft("");
      setReplyTo(null);
      setAttachments([]);
    } catch (e) {
      alert(e?.response?.data?.detail || "Send failed.");
    }
    setSending(false);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const updated = await teamChatApi.edit(editing.id, body);
      setMessages(m => m.map(x => x.id === updated.id ? updated : x));
      setEditing(null);
      setDraft("");
    } catch (e) { alert(e?.response?.data?.detail || "Edit failed."); }
    setSending(false);
  };

  const onReply = (m) => {
    setReplyTo({ id: m.id, body: m.body, sender: m.sender });
    composerRef.current?.focus();
  };
  const onEdit = (m) => {
    setEditing(m);
    setDraft(m.body || "");
    composerRef.current?.focus();
  };
  const onDelete = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    await teamChatApi.remove(m.id);
    setMessages(arr => arr.map(x => x.id === m.id ? { ...x, deleted: true, body: "", attachments: [] } : x));
  };
  const onReact = async (msgId, emoji) => {
    const res = await teamChatApi.toggleReaction(msgId, emoji);
    setMessages(arr => arr.map(x => x.id === msgId ? { ...x, reactions: res.reactions } : x));
  };

  // ---- Slash AI ----
  const runAI = async (intent) => {
    if (!activeRoom) return;
    setAiBusy(true);
    setShowSlash(false);
    try {
      const r = await teamChatApi.aiAssist(activeRoom.id, intent, intent === "polish" ? draft : "");
      if (intent === "summarize") {
        // Show as a non-sent panel by inserting a synthetic local-only message
        setMessages(m => [...m, {
          id: `local-ai-${Date.now()}`,
          room_id: activeRoom.id,
          sender: "hexa-ai@hexawebos.com",
          body: r.text,
          system: false,
          reactions: {}, attachments: [], reply_to: null, edited: false, deleted: false,
          created: new Date().toISOString(),
          _localAi: true,
        }]);
      } else {
        // draft_reply or polish → replace composer text
        setDraft(r.text);
      }
    } catch (e) { alert(e?.response?.data?.detail || "AI unavailable."); }
    setAiBusy(false);
  };

  // ---- Typing indicator ----
  const onDraftChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    if (v.startsWith("/")) setShowSlash(true); else setShowSlash(false);
    if (activeRoom) chatBus.sendTyping(activeRoom.id);
  };

  const typingNames = useMemo(() => {
    const keys = Object.keys(typingFrom);
    if (!keys.length || !activeRoom) return "";
    const labels = keys.map(addr => {
      if (activeRoom.type === "dm") return activeRoom.other?.name || addr.split("@")[0];
      return addr.split("@")[0];
    });
    return `${labels.join(", ")} is typing…`;
  }, [typingFrom, activeRoom]);

  // ---- Group messages by date headers ----
  const grouped = useMemo(() => {
    const out = [];
    let lastDate = "";
    for (const m of messages) {
      const d = new Date(m.created).toDateString();
      if (d !== lastDate) {
        out.push({ _date: d, key: `date-${d}` });
        lastDate = d;
      }
      out.push(m);
    }
    return out;
  }, [messages]);

  const senderName = (addr) => {
    if (addr === me) return "You";
    if (addr === "hexa-ai@hexawebos.com") return "HEXA AI";
    if (activeRoom?.type === "dm") return activeRoom.other?.name || addr.split("@")[0];
    return addr.split("@")[0];
  };

  // ---- Mention insert helper ----
  const insertMention = () => {
    if (!activeRoom || activeRoom.type !== "space") return;
    const candidates = activeRoom.members.filter(a => a !== me);
    if (!candidates.length) return;
    const choice = window.prompt(`Mention which teammate?\n\n${candidates.join("\n")}`, candidates[0]);
    if (choice) setDraft(d => `${d}${d && !d.endsWith(" ") ? " " : ""}@${choice} `);
  };

  return (
    <div className="h-full flex flex-col md:flex-row bg-[#0b1014]" data-testid="app-teamchat">
      {/* Left: rooms list */}
      <div className="w-full md:w-60 h-44 md:h-auto border-b md:border-b-0 md:border-r border-white/10 flex flex-col bg-black/30">
        <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
          <span className="mono text-[10px] uppercase tracking-widest text-[#8b5cf6]">// team chat</span>
          <button
            data-testid="newroom-open"
            onClick={() => setShowNew(true)}
            className="btn-ghost p-1 text-[#8b5cf6] hover:bg-[#8b5cf6]/15"
            title="New conversation"
          ><Plus size={12} /></button>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {rooms.length === 0 && (
            <div className="text-center text-[10px] text-white/40 italic py-8 px-3 mono">
              // no conversations yet<br />click + to start
            </div>
          )}
          {/* DMs */}
          {rooms.filter(r => r.type === "dm").length > 0 && (
            <div className="mb-2">
              <div className="px-3 py-1 mono text-[9px] uppercase tracking-widest text-white/35">Direct</div>
              {rooms.filter(r => r.type === "dm").map(r => (
                <RoomRow key={r.id} room={r} active={activeId === r.id} onClick={() => setActiveId(r.id)} online={presence.has(r.other?.address)} />
              ))}
            </div>
          )}
          {/* Spaces */}
          {rooms.filter(r => r.type === "space").length > 0 && (
            <div className="mb-2">
              <div className="px-3 py-1 mono text-[9px] uppercase tracking-widest text-white/35">Spaces</div>
              {rooms.filter(r => r.type === "space").map(r => (
                <RoomRow key={r.id} room={r} active={activeId === r.id} onClick={() => setActiveId(r.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right: active conversation */}
      <div className="flex-1 flex flex-col min-w-0">
        {!activeRoom ? (
          <div className="flex-1 flex items-center justify-center text-white/40 mono text-xs uppercase tracking-widest">
            // pick a conversation or start a new one
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-4 py-2 border-b border-white/10 flex items-center gap-3 bg-black/20">
              <RoomIcon room={activeRoom} online={presence.has(activeRoom.other?.address)} />
              <div className="flex-1 min-w-0">
                <div data-testid="chat-room-title" className="text-sm font-medium truncate">
                  {activeRoom.type === "space" ? activeRoom.name : (activeRoom.other?.name || activeRoom.other?.address)}
                </div>
                <div className="text-[10px] text-white/45 mono uppercase tracking-widest">
                  {activeRoom.type === "space"
                    ? `${activeRoom.members.length} members`
                    : (presence.has(activeRoom.other?.address) ? "online" : "offline")}
                </div>
              </div>
              <button
                disabled={aiBusy}
                onClick={() => runAI("summarize")}
                className="btn-ghost text-[10px] mono uppercase tracking-widest flex items-center gap-1 px-2 py-1 border border-[#8b5cf6]/25 hover:border-[#8b5cf6]/60 disabled:opacity-40"
                data-testid="chat-ai-summarize"
                title="Summarize conversation"
              >
                {aiBusy ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />} Summarize
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto py-2 space-y-0.5">
              {grouped.length === 0 && (
                <div className="text-center text-white/40 mono text-[10px] uppercase tracking-widest pt-12">
                  // no messages yet · say hi 👋
                </div>
              )}
              {grouped.map((item) => {
                if (item._date) {
                  return (
                    <div key={item.key} className="flex items-center gap-2 px-4 py-2">
                      <div className="flex-1 h-px bg-white/8" />
                      <div className="mono text-[9px] uppercase tracking-widest text-white/35">{item._date}</div>
                      <div className="flex-1 h-px bg-white/8" />
                    </div>
                  );
                }
                if (item._localAi) {
                  return (
                    <div key={item.id} className="mx-4 my-2 p-2 border border-[#a78bfa]/40 bg-[#a78bfa]/8">
                      <div className="mono text-[9px] uppercase tracking-widest text-[#a78bfa] flex items-center gap-1 mb-1">
                        <Sparkles size={10} /> HEXA AI · summary
                      </div>
                      <div className="text-xs whitespace-pre-wrap text-white/85">{item.body}</div>
                    </div>
                  );
                }
                return (
                  <MessageBubble
                    key={item.id}
                    msg={item}
                    isMine={item.sender === me}
                    onReply={onReply}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onReact={onReact}
                    senderName={senderName(item.sender)}
                  />
                );
              })}
              {typingNames && (
                <div data-testid="typing-indicator" className="px-4 mono text-[10px] uppercase tracking-widest text-white/45 animate-pulse">// {typingNames}</div>
              )}
            </div>

            {/* Reply / Edit banner */}
            {replyTo && (
              <div className="px-3 py-1.5 border-t border-white/10 bg-[#8b5cf6]/8 flex items-center gap-2">
                <Reply size={11} className="text-[#8b5cf6]" />
                <span className="text-[10px] text-white/65 truncate flex-1">replying to <em className="text-white/85">{(replyTo.body || "").slice(0, 80)}</em></span>
                <button onClick={() => setReplyTo(null)} className="text-white/50 hover:text-white"><X size={11} /></button>
              </div>
            )}
            {editing && (
              <div className="px-3 py-1.5 border-t border-white/10 bg-[#3b82f6]/8 flex items-center gap-2">
                <Pencil size={11} className="text-[#3b82f6]" />
                <span className="text-[10px] text-white/65 flex-1">editing message</span>
                <button onClick={() => { setEditing(null); setDraft(""); }} className="text-white/50 hover:text-white"><X size={11} /></button>
              </div>
            )}
            {attachments.length > 0 && (
              <div className="px-3 py-1.5 border-t border-white/10 bg-white/3 flex items-center gap-2 flex-wrap">
                {attachments.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] mono bg-[#8b5cf6]/15 text-[#a78bfa] border border-[#8b5cf6]/30">
                    <Paperclip size={9} />{a.name}
                    <button onClick={() => setAttachments(arr => arr.filter((_, idx) => idx !== i))} className="hover:text-[#f87171]"><X size={9} /></button>
                  </span>
                ))}
              </div>
            )}

            {/* Composer */}
            <div className="border-t border-white/10 p-2 relative">
              {showSlash && (
                <div data-testid="slash-menu" className="absolute left-2 right-2 bottom-full mb-1 bg-[#0b1014] border border-[#a78bfa]/40 z-10">
                  <button onClick={() => runAI("summarize")} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-[#a78bfa]/10 flex items-center gap-2">
                    <Sparkles size={11} className="text-[#a78bfa]" /> <span className="mono uppercase tracking-widest text-[10px]">/summarize</span> <span className="text-white/45 ml-auto">recap the conversation</span>
                  </button>
                  <button onClick={() => runAI("draft_reply")} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-[#a78bfa]/10 flex items-center gap-2">
                    <Sparkles size={11} className="text-[#a78bfa]" /> <span className="mono uppercase tracking-widest text-[10px]">/draft</span> <span className="text-white/45 ml-auto">draft my next reply</span>
                  </button>
                  <button onClick={() => runAI("polish")} className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-[#a78bfa]/10 flex items-center gap-2">
                    <Sparkles size={11} className="text-[#a78bfa]" /> <span className="mono uppercase tracking-widest text-[10px]">/polish</span> <span className="text-white/45 ml-auto">rewrite my draft</span>
                  </button>
                </div>
              )}
              <div className="flex items-center gap-1">
                <button
                  data-testid="composer-attach"
                  onClick={() => fileRef.current?.click()}
                  className="btn-ghost p-2 text-white/55 hover:text-[#8b5cf6]"
                  title="Attach"
                ><Paperclip size={14} /></button>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={onPickFiles} />
                {activeRoom.type === "space" && (
                  <button
                    data-testid="composer-mention"
                    onClick={insertMention}
                    className="btn-ghost p-2 text-white/55 hover:text-[#8b5cf6]"
                    title="Mention"
                  ><AtSign size={14} /></button>
                )}
                <input
                  ref={composerRef}
                  data-testid="composer-input"
                  value={draft}
                  onChange={onDraftChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    if (e.key === "Escape") { setReplyTo(null); setEditing(null); setShowSlash(false); }
                  }}
                  placeholder={editing ? "Edit message…" : `Message ${activeRoom.type === "space" ? "#" + activeRoom.name : senderName(activeRoom.other?.address || "")}…   (try /summarize)`}
                  className="flex-1 bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm outline-none focus:border-[#8b5cf6]/40"
                />
                <button
                  data-testid="composer-send"
                  onClick={send}
                  disabled={sending || (!draft.trim() && attachments.length === 0)}
                  className="p-2 disabled:opacity-30"
                  style={{ background: "linear-gradient(135deg, #a78bfa, #a78bfa)", color: "#0b1014" }}
                  title={editing ? "Save edit" : "Send"}
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : (editing ? <Check size={14} /> : <Send size={14} />)}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showNew && (
        <NewRoomModal
          onClose={() => setShowNew(false)}
          onCreated={(room) => {
            setShowNew(false);
            // Insert/refresh in list and select it
            setRooms(rs => rs.some(r => r.id === room.id) ? rs : [room, ...rs]);
            setActiveId(room.id);
          }}
        />
      )}
    </div>
  );
}

function RoomRow({ room, active, onClick, online }) {
  return (
    <button
      data-testid={`room-${room.id}`}
      onClick={onClick}
      className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs ${active ? "bg-[#8b5cf6]/12 border-l-2 border-[#8b5cf6]" : "hover:bg-white/5 border-l-2 border-transparent"}`}
    >
      <RoomIcon room={room} online={online} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span className="truncate text-white/85">
            {room.type === "space" ? room.name : (room.other?.name || room.other?.address || "(unknown)")}
          </span>
          {room.unread > 0 && (
            <span data-testid={`unread-${room.id}`} className="ml-auto inline-flex items-center justify-center min-w-[16px] h-[14px] px-1 text-[9px] mono bg-[#3b82f6] text-[#0b1014] font-medium">
              {room.unread}
            </span>
          )}
        </div>
        {room.last_preview && (
          <div className="truncate text-[10px] text-white/45">{room.last_preview}</div>
        )}
      </div>
    </button>
  );
}
